import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { isEqual } from 'lodash';
import { up } from 'styled-breakpoints';
import styled, { css } from 'styled-components';
import * as zod from 'zod';
import { dayjs } from '@lgg/isomorphic/dayjs';
import { getDatesDifferenceInUnits } from '@lgg/isomorphic/i18n/date-utils';
import { roundToMinutesInterval } from '@lgg/isomorphic/round-to-minutes-interval';
import {
  Query,
  QueryTaskArgs,
  Task,
  TaskCategory,
  TaskStatus,
} from '@lgg/isomorphic/types/__generated__/graphql';
import { useShowConfirmationModal } from 'src/components/general/feedback/hooks/use-show-confirmation-modal';
import { useShowNotification } from 'src/components/general/feedback/hooks/use-show-notification';
import { Icon } from 'src/components/general/icon';
import { LggDateTimePicker } from 'src/components/general/inputs/custom-date-pickers/lgg-date-time-picker';
import {
  InlineTextArea,
  BigTextInput,
  InlineInputWrapper,
} from 'src/components/general/inputs/inline-text-inputs';
import { Select, SelectOption } from 'src/components/general/inputs/select/select';
import { InlineFormModal } from 'src/components/general/modals/inline-form/inline-form-modal';
import { InlineFormSelect } from 'src/components/general/modals/inline-form/inline-form-select';
import {
  InlineFormUserSelect,
  InlineFormContactSelect,
  UserSelectPlaceholder,
  UserSelectValue,
  useShowCloseConfirmationModal,
  InlineFormDeleteOption,
} from 'src/components/general/modals/inline-form/shared';
import { FlexColumn } from 'src/components/layout/flex-column';
import { FlexRow } from 'src/components/layout/flex-row';
import {
  useTaskCategoryOptions,
  useTaskReminderAtOptions,
  useTaskStatusOptions,
} from 'src/components/pages/tasks/hooks';
import { useDeleteTask } from 'src/hooks/gql/use-delete-task';
import { useAuthorization } from 'src/hooks/use-authorization';
import { useBreakpoint } from 'src/hooks/use-breakpoint';
import { useCurrentInstitution } from 'src/hooks/use-current-institution';
import { useCurrentUser } from 'src/hooks/use-current-user';
import { useHandleGraphQLError } from 'src/hooks/use-handle-graphql-error';
import { useModalUrl } from 'src/hooks/use-modal-url';
import { useUrls } from 'src/hooks/use-urls';
import { useVisible } from 'src/hooks/use-visible';
import { copyUrlToClipboard } from 'src/utils/copy-to-clipboard-helpers';

const TASK_FIELDS_FRAGMENT = gql`
  fragment TaskFields on Task {
    id
    title
    description
    category
    assignedTo {
      id
      fullName
    }
    contact {
      id
      label
    }
    status
    dueAt
    reminderAt
  }
`;

const CREATE_TASK_MUTATION = gql`
  mutation CreateTask($input: CreateTaskInput!) {
    createTask(input: $input) {
      task {
        id
      }
    }
  }
`;

const UPDATE_TASK_MUTATION = gql`
  ${TASK_FIELDS_FRAGMENT}
  mutation UpdateTask($input: UpdateTaskInput!) {
    updateTask(input: $input) {
      task {
        ...TaskFields
      }
    }
  }
`;

const GET_TASK = gql`
  ${TASK_FIELDS_FRAGMENT}
  query GetTask($id: Int!) {
    task(id: $id) {
      ...TaskFields
    }
  }
`;

const createTaskSchema = zod.object({
  title: zod.string(),
  description: zod.string().max(100).nullable(),
  category: zod.string(),
  assignedTo: zod
    .number()
    .nullable()
    .refine((value): boolean => value !== null),
  contactId: zod.number().nullable(),
  status: zod.enum(['COMPLETED', 'IN-PROGRESS', 'YET-TO-START']),
  dueAtDate: zod.string().min(1),
  reminderAt: zod.number().nullable(),
});

type CreateTaskFormValues = zod.infer<typeof createTaskSchema>;

const BottomContentContainer = styled(FlexRow)`
  gap: 8px;
  height: 35px;
  flex-wrap: wrap;
  min-height: 35px;
  height: max-content;
`;

const TitleInputContainer = styled(FlexColumn)`
  margin: 8px 10px;
  width: 100%;
`;

const NotificationIcon = styled(Icon)`
  margin-right: 5px;

  svg {
    height: 16px;
    width: 16px;

    path {
      fill: ${({ theme }) => theme.colors.flint};
    }
  }
`;

export const DesktopSelect = styled(Select)`
  width: max-content;

  .lgg-select__control {
    min-height: unset;
    height: 30px;
    padding: 6px 8px;
    width: max-content;

    .lgg-select__indicators {
      display: none;
    }

    .lgg-select__single-value {
      display: flex;
      align-items: center;
      line-height: 17px;
    }
  }

  .lgg-select__menu {
    width: max-content;
    min-width: unset;
  }
`;

const DueAtSelectContainer = styled.span<{ $isOverdue: boolean }>`
  width: max-content;

  & > .lgg-date-time-picker-input {
    & > .ant-picker {
      height: 30px;
      max-width: 175px;
      padding: 6px;

      & > .ant-picker-input {
        & > input {
          field-sizing: content;
          order: 2;

          ${({ $isOverdue }) =>
            $isOverdue &&
            css`
              color: ${({ theme }) => theme.colors.secondaryCoral};
            `}
        }

        .ant-picker-suffix {
          margin-left: 0;
          margin-right: 5px;
          order: 1;
        }
      }
    }
  }
`;

const StatusValueWrapper = styled.span<{ $isCompleted: boolean }>`
  ${({ $isCompleted }) =>
    $isCompleted &&
    css`
      .lgg-select__control {
        background-color: ${({ theme }) => theme.colors.secondaryMint10};

        .lgg-select__single-value {
          .lgg-select__single-value-text {
            color: ${({ theme }) => theme.colors.secondaryMintDark};
          }

          svg {
            path {
              fill: ${({ theme }) => theme.colors.secondaryMintDark};
            }
          }
        }
      }
    `}
`;

const getTaskDefaultValues = (task: Task): CreateTaskFormValues => {
  const reminderAt = task.reminderAt
    ? getDatesDifferenceInUnits(task.reminderAt, task.dueAt).minutes
    : null;

  return {
    title: task.title,
    description: task.description ?? '',
    category: task.category,
    assignedTo: task.assignedTo?.id ?? null,
    contactId: task.contact?.id ?? null,
    status: task.status.replaceAll('_', '-') as
      | 'COMPLETED'
      | 'IN-PROGRESS'
      | 'YET-TO-START',
    dueAtDate: task.dueAt,
    reminderAt,
  };
};

// Free-text inputs are saved when the user leaves them rather than while typing.
const AUTOSAVE_ON_BLUR_FIELDS: Path<CreateTaskFormValues>[] = ['title', 'description'];

export const TaskInlineFormModal = memo(() => {
  const currentInstitution = useCurrentInstitution();
  const { id: currentUserId } = useCurrentUser();
  const modalVisibilityHandler = useVisible(true);
  const { goBack } = useModalUrl({
    backgroundLocation: `/i/company/${currentInstitution.id}/cms/tasks`,
  });
  const breakpointUpMd = useBreakpoint(up('md'));
  const { t } = useTranslation(['common', 'tasks']);
  const handlecloseModal = () => {
    createTaskForm.reset();
    goBack();
    modalVisibilityHandler.close();
  };
  const taskCategoryOptions = useTaskCategoryOptions();
  const taskStatusOptions = useTaskStatusOptions();
  const taskReminderAtOptions = useTaskReminderAtOptions();
  const { taskId } = useParams<{
    taskId?: string;
  }>();
  const searchParams = new URLSearchParams(window.location.search);
  const targetDate = searchParams.get('date');
  const targetContact = searchParams.get('contactId');
  const parsedTargetDate = targetDate ? dayjs(targetDate) : undefined;
  const roundedTargetDate = parsedTargetDate
    ? roundToMinutesInterval(parsedTargetDate)
    : undefined;
  const isCreatingTask = !taskId;
  const showNotification = useShowNotification();
  const showConfirmationModal = useShowConfirmationModal();
  const { getTaskEditUrl } = useUrls();

  const handleGetTaskError = useCallback(() => {
    modalVisibilityHandler.close();
    goBack();
    showNotification({
      title: t('tasks:taskNotFoundError.title'),
      message: t('tasks:taskNotFoundError.message'),
      type: 'error',
    });
  }, [modalVisibilityHandler, goBack, showNotification, t]);

  const { hasPermission } = useAuthorization();
  const hasTaskManagePermission = hasPermission('crm:company.task:update:all');

  const [isTaskOwner, setIsTaskOwner] = useState(false);

  const handleGraphQLError = useHandleGraphQLError();
  const showCloseConfirmationModal = useShowCloseConfirmationModal();

  const [defaultValues, setDefaultValues] = useState<CreateTaskFormValues>({
    assignedTo: null,
    contactId: null,
    dueAtDate: roundedTargetDate?.toISOString() ?? '',
    title: '',
    description: '',
    category: 'TO_DO',
    status: 'YET-TO-START',
    reminderAt: null,
  });

  const createTaskForm = useForm<CreateTaskFormValues>({
    resolver: zodResolver(
      createTaskSchema.extend({
        title: zod
          .string()
          .min(1, t('tasks:taskInlineForm.fields.title.errors.required'))
          .max(100, t('tasks:taskInlineForm.fields.title.errors.maxLength')),
        description: zod
          .string()
          .max(100, t('tasks:taskInlineForm.fields.description.errors.maxLength')),
      }),
    ),
    defaultValues,
  });

  useEffect(() => {
    if (isCreatingTask) {
      createTaskForm.setValue('contactId', Number(targetContact));
      createTaskForm.setValue('assignedTo', currentUserId);
    }
  }, [isCreatingTask, targetContact, currentUserId, createTaskForm]);

  const hasLoadedTaskRef = useRef(false);

  const { loading: getTaskLoading } = useQuery<Pick<Query, 'task'>, QueryTaskArgs>(
    GET_TASK,
    {
      variables: { id: Number(taskId) },
      skip: !taskId,
      onCompleted: ({ task }) => {
        if (!task) {
          handleGetTaskError();
          return;
        }

        if (hasLoadedTaskRef.current) {
          // onCompleted re-fires whenever Apollo's cache broadcasts a change to
          // this task's normalized entity, not just on the initial load (e.g. if
          // a future update-mutation response ever returns more than `id`, this
          // would otherwise reset the form and clobber in-progress edits).
          return;
        }
        hasLoadedTaskRef.current = true;

        if (task.assignedTo?.id === currentUserId) {
          setIsTaskOwner(true);
        }

        const values = getTaskDefaultValues(task);

        setDefaultValues(values);
        createTaskForm.reset(values);
      },
      onError: handleGetTaskError,
    },
  );

  const [createTask, { loading: createTaskLoading }] = useMutation(CREATE_TASK_MUTATION);
  const [updateTask, { loading: updateTaskLoading }] = useMutation(UPDATE_TASK_MUTATION);
  const [deleteTask, { loading: deleteTaskLoading }] = useDeleteTask();

  const {
    formState: { isDirty, dirtyFields },
  } = createTaskForm;

  const isFormDirty = isDirty && Object.keys(dirtyFields).length > 0;

  const statusSelect = useMemo(() => {
    return (
      <Controller
        control={createTaskForm.control}
        name="status"
        render={({ field }) => {
          const isCompleted = field.value === 'COMPLETED';
          const error = createTaskForm.formState.errors.status?.message;
          const props = {
            name: 'upsert-task-field-status',
            mobileLabel: t('tasks:taskInlineForm.fields.status.label'),
            value: taskStatusOptions.find((option) => option.value === field.value),
            hasError: Boolean(error),
            isClearable: false,
            isMulti: false,
            isSearchable: false,
            options: taskStatusOptions,
            hideIndicators: true,
            onChange: (value) => {
              field.onChange((value as SelectOption<string>)?.value);
            },
          };

          return breakpointUpMd ? (
            <StatusValueWrapper $isCompleted={isCompleted}>
              <DesktopSelect {...field} {...props} />
            </StatusValueWrapper>
          ) : (
            <InlineInputWrapper
              label={t('tasks:taskInlineForm.fields.status.label')}
              error={error}
              icon="status"
            >
              <InlineFormSelect {...props} />
            </InlineInputWrapper>
          );
        }}
      />
    );
  }, [
    breakpointUpMd,
    createTaskForm.control,
    createTaskForm.formState.errors.status?.message,
    t,
    taskStatusOptions,
  ]);

  const reminderAtSelect = useMemo(() => {
    return (
      <Controller
        control={createTaskForm.control}
        name="reminderAt"
        render={({ field }) => {
          const error = createTaskForm.formState.errors.reminderAt?.message;
          const props = {
            name: 'upsert-task-field-reminder-at',
            mobileLabel: t('tasks:taskInlineForm.fields.reminderAt.label'),
            value: taskReminderAtOptions.find((option) => option.value === field.value),
            hasError: Boolean(error),
            placeholder: t('tasks:taskInlineForm.fields.reminderAt.placeholder'),
            isSearchable: false,
            options: taskReminderAtOptions,
            hideIndicators: true,
            onChange: (value) => {
              if (value) {
                field.onChange(value.value === field.value ? null : value.value);
              }
            },
            renderValue: (value) => {
              return (
                <>
                  <NotificationIcon type="notification" />
                  <span>{value?.label}</span>
                </>
              );
            },
            renderPlaceholder: () => (
              <FlexRow style={{ alignItems: 'center' }}>
                <NotificationIcon type="notification" />
                <span>{t('tasks:taskInlineForm.fields.reminderAt.placeholder')}</span>
              </FlexRow>
            ),
          };

          return breakpointUpMd ? (
            <DesktopSelect {...field} {...props} />
          ) : (
            <InlineInputWrapper
              label={t('tasks:taskInlineForm.fields.reminderAt.label')}
              error={error}
              icon="notification"
            >
              <InlineFormSelect {...props} />
            </InlineInputWrapper>
          );
        }}
      />
    );
  }, [
    breakpointUpMd,
    createTaskForm.control,
    createTaskForm.formState.errors.reminderAt?.message,
    t,
    taskReminderAtOptions,
  ]);

  const canManageTask = useMemo(() => {
    return isTaskOwner || hasTaskManagePermission;
  }, [isTaskOwner, hasTaskManagePermission]);

  const dueAtSelect = useMemo(() => {
    return (
      <Controller
        control={createTaskForm.control}
        name="dueAtDate"
        render={({ field }) => {
          const error = createTaskForm.formState.errors.dueAtDate?.message;
          const compontent = (
            <LggDateTimePicker
              showPresetOptions
              allowTimeSelection
              inputValue={field.value ? dayjs(field.value).toDate() : undefined}
              inputDateFormat="MMM D, YYYY. hh:mm a"
              onApply={(date) => {
                field.onChange(date ? date.toISOString() : undefined);
              }}
              placeholder={t('tasks:taskInlineForm.fields.dueDate.placeholder')}
              testId="upsert-task-field-dueDate"
              hasError={Boolean(error)}
              disabledDate={(date) => date.isBefore(dayjs())}
              showSuffixIcon={breakpointUpMd}
              allowClear={false}
            />
          );

          return breakpointUpMd ? (
            <DueAtSelectContainer
              $isOverdue={dayjs(field.value).isBefore(dayjs())}
              data-lgg-id="upsert-task-field-dueDate-container"
            >
              {compontent}
            </DueAtSelectContainer>
          ) : (
            <InlineInputWrapper
              label={t('tasks:taskInlineForm.fields.dueDate.label')}
              error={error}
              showErrorMessage={false}
              icon="calendarNoPadding"
            >
              {compontent}
            </InlineInputWrapper>
          );
        }}
      />
    );
  }, [
    breakpointUpMd,
    createTaskForm.control,
    createTaskForm.formState.errors.dueAtDate?.message,
    t,
  ]);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const updateTaskRecord = useCallback(
    async (
      values: Partial<CreateTaskFormValues>,
      { onCompleted }: { onCompleted: () => void },
    ) => {
      const sharedVariables = {
        ...values,
        ...(values.description !== undefined && {
          description: values.description ?? '',
        }),
        ...(values.status !== undefined && {
          status: values.status.replaceAll('-', '_') as TaskStatus,
        }),
        ...(values.category !== undefined && {
          category: values.category.replaceAll('-', '_') as TaskCategory,
        }),
      };

      await updateTask({
        variables: {
          input: {
            ...sharedVariables,
            taskId: Number(taskId),
          },
        },
        onCompleted,
        onError: handleGraphQLError,
      });
    },
    [updateTask, taskId, handleGraphQLError],
  );

  const onAutosaveTask = useCallback(
    async (values: Partial<CreateTaskFormValues>) => {
      await updateTaskRecord(values, {
        onCompleted: () => {
          (Object.keys(values) as (keyof CreateTaskFormValues)[]).forEach((name) => {
            // If the field changed again while the mutation was in flight, don't
            // mark it clean: leave it dirty so the next autosave cycle retries it.
            if (isEqual(createTaskForm.getValues(name), values[name])) {
              createTaskForm.resetField(name, { defaultValue: values[name] });
            }
          });
        },
      });
    },
    [updateTaskRecord, createTaskForm],
  );

  return (
    <InlineFormModal<CreateTaskFormValues>
      title={
        isCreatingTask
          ? t('tasks:taskInlineForm.title.create')
          : t('tasks:taskInlineForm.title.update')
      }
      submitButtonText={t('tasks:taskInlineForm.submitButtonText.create')}
      testId="create-task-modal"
      isLoading={getTaskLoading}
      disableSubmitButton={!isCreatingTask && !canManageTask}
      isSubmitting={createTaskLoading || updateTaskLoading}
      visibilityHandler={modalVisibilityHandler}
      form={createTaskForm}
      onCancel={() => {
        if (!isFormDirty) {
          handlecloseModal();
          return;
        }

        showCloseConfirmationModal({
          onCancel: handlecloseModal,
        });
      }}
      onSubmit={async (values) => {
        const sharedVariables = {
          ...values,
          description: values.description ?? '',
          contactId: values.contactId ? Number(values.contactId) : null,
          status: values.status?.replaceAll('-', '_') as TaskStatus,
          category: values.category.replaceAll('-', '_') as TaskCategory,
        };

        await createTask({
          variables: {
            input: {
              institutionId: currentInstitution.id,
              ...sharedVariables,
            },
          },
          onCompleted: () => {
            showNotification({
              title: t('tasks:taskCreated'),
              type: 'success',
            });
            handlecloseModal();
          },
          onError: handleGraphQLError,
        });
      }}
      autoSaveEnabled={!isCreatingTask && canManageTask}
      onAutosave={onAutosaveTask}
      autosaveOnBlurFields={AUTOSAVE_ON_BLUR_FIELDS}
      topContent={
        <Controller
          control={createTaskForm.control}
          name="title"
          render={({ field }) => (
            <TitleInputContainer>
              <BigTextInput
                {...field}
                data-lgg-id="upsert-task-field-title"
                showErrorMessage
                error={createTaskForm.formState.errors.title?.message}
                placeholder={t('tasks:taskInlineForm.fields.title.placeholder')}
                label={t('tasks:taskInlineForm.fields.title.label')}
              />
            </TitleInputContainer>
          )}
        />
      }
      bottomContent={
        breakpointUpMd ? (
          <BottomContentContainer>
            {statusSelect}
            {dueAtSelect}
            {reminderAtSelect}
          </BottomContentContainer>
        ) : null
      }
      moreOptions={
        isCreatingTask
          ? []
          : [
              {
                icon: 'link',
                label: t('tasks:taskInlineForm.actions.copyTaskLink.label'),
                onClick: () => {
                  copyUrlToClipboard(
                    getTaskEditUrl({
                      taskId: Number(taskId),
                      options: { includeHost: true },
                    }),
                  );
                  showNotification({
                    title: t('tasks:taskInlineForm.actions.copyTaskLink.successMessage'),
                    type: 'success',
                  });
                },
              },
              ...(canManageTask
                ? [
                    {
                      label: t('tasks:taskInlineForm.actions.deleteTask.label'),
                      render: () => {
                        return (
                          <InlineFormDeleteOption
                            label={t('tasks:taskInlineForm.actions.deleteTask.label')}
                            icon="deleteBin"
                            disabled={deleteTaskLoading}
                            onClick={async () => {
                              showConfirmationModal({
                                title: t(
                                  'tasks:taskInlineForm.actions.deleteTask.confirmationTitle',
                                ),
                                message: t(
                                  'tasks:taskInlineForm.actions.deleteTask.confirmationMessage',
                                ),
                                confirmButtonText: t(
                                  'tasks:taskInlineForm.actions.deleteTask.label',
                                ),
                                confirmButtonType: 'delete',
                                testId: 'delete-task-confirmation-modal',
                                onConfirm: async () => {
                                  await deleteTask({
                                    variables: {
                                      input: {
                                        taskId: Number(taskId),
                                      },
                                    },
                                    onCompleted: () => {
                                      handlecloseModal();
                                    },
                                    onError: (error) => {
                                      handleGraphQLError(error);
                                    },
                                    update(cache) {
                                      const normalizedId = cache.identify({
                                        id: Number(taskId),
                                        __typename: 'Task',
                                      });
                                      cache.evict({ id: normalizedId });
                                      cache.gc();
                                    },
                                  });
                                },
                              });
                            }}
                          />
                        );
                      },
                    },
                  ]
                : []),
            ]
      }
    >
      <FlexColumn>
        {breakpointUpMd ? null : (
          <>
            {statusSelect}
            {dueAtSelect}
          </>
        )}
        <Controller
          control={createTaskForm.control}
          name="description"
          render={({ field }) => {
            const error = createTaskForm.formState.errors.description?.message;

            return (
              <InlineTextArea
                {...field}
                label={t('tasks:taskInlineForm.fields.description.label')}
                icon="draft"
                error={error}
                ref={textAreaRef}
                placeholder={t('tasks:taskInlineForm.fields.description.placeholder')}
                data-lgg-id="upsert-task-field-description"
                onChange={(e) => {
                  const value = e.target.value;

                  field.onChange(value);
                }}
                onBlur={(e) => {
                  e.currentTarget.scrollTo({ top: 0, behavior: 'smooth' });
                }}
              />
            );
          }}
        />
        <Controller
          control={createTaskForm.control}
          name="category"
          render={({ field }) => {
            const error = createTaskForm.formState.errors.category?.message;
            const fieldValue = field.value.replaceAll('-', '_');

            return (
              <InlineInputWrapper
                label={t('tasks:taskInlineForm.fields.category.label')}
                error={error}
                icon="list"
              >
                <InlineFormSelect
                  {...field}
                  name="upsert-task-field-category"
                  mobileLabel={t('tasks:taskInlineForm.fields.category.label')}
                  value={taskCategoryOptions.find(
                    (option) => option.value === fieldValue,
                  )}
                  hasError={Boolean(error)}
                  isClearable={false}
                  isSearchable={false}
                  options={taskCategoryOptions}
                  hideIndicators
                  onChange={(value) => {
                    field.onChange((value as SelectOption<string>)?.value);
                  }}
                />
              </InlineInputWrapper>
            );
          }}
        />
        <Controller
          control={createTaskForm.control}
          name="assignedTo"
          render={({ field }) => {
            const error = createTaskForm.formState.errors.assignedTo?.message;

            return (
              <InlineInputWrapper
                label={t('tasks:taskInlineForm.fields.assignee.label')}
                error={error}
                icon="assignedto"
                showErrorMessage={false}
              >
                <InlineFormUserSelect
                  mobileLabel={t('tasks:taskInlineForm.fields.assignee.label')}
                  selectedUsersIds={field.value ? [field.value] : []}
                  hasError={Boolean(error)}
                  isMulti={false}
                  isClearable={!breakpointUpMd}
                  onChange={(value) => {
                    if (value) {
                      field.onChange(value.value === field.value ? null : value.value);
                    }
                  }}
                  name="upsert-task-field-assigned-to"
                  hideIndicators
                  renderValue={(value) => <UserSelectValue value={value} />}
                  renderPlaceholder={() => (
                    <UserSelectPlaceholder
                      placeholder={t('tasks:taskInlineForm.fields.assignee.placeholder')}
                    />
                  )}
                />
              </InlineInputWrapper>
            );
          }}
        />
        <Controller
          control={createTaskForm.control}
          name="contactId"
          render={({ field }) => {
            const error = createTaskForm.formState.errors.contactId?.message;

            return (
              <InlineInputWrapper
                label={t('tasks:taskInlineForm.fields.relatedContact.label')}
                error={error}
                icon="contactAlt"
              >
                <InlineFormContactSelect
                  selectedContactIds={field.value ? [field.value] : []}
                  isMulti={false}
                  onChange={(value) => {
                    if (value) {
                      field.onChange(value.value === field.value ? null : value.value);
                    }
                  }}
                  isClearable={false}
                  isDisabled={!isCreatingTask}
                  hideIndicators
                  name="upsert-task-field-related-contact"
                  mobileLabel={t('tasks:taskInlineForm.fields.relatedContact.label')}
                />
              </InlineInputWrapper>
            );
          }}
        />
        {breakpointUpMd ? null : reminderAtSelect}
      </FlexColumn>
    </InlineFormModal>
  );
});
